import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useApiErrorHandler from '../hooks/useApiErrorHandler';
import LoadingSpinner from './common/LoadingSpinner';
import Alert from './common/Alert';
import dashboardService from '../services/DashboardService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import UserAccountMenu from './common/UserAccountMenu';
import ProcesosMenu from './common/ProcesosMenu';
import SolicitudesProcesosBadge from './common/SolicitudesProcesosBadge';
import './NewDashboard.css';
import {
  isGptClassicEnabled as hasGptClassicFeature,
  isOpenAIAssistantEnabled as hasOpenAIAssistantFeature,
  isRagFlowAssistantEnabled as hasRagFlowAssistantFeature
} from '../utils/adminMenu';
import useRegionalFeatureFlags from '../hooks/useRegionalFeatureFlags';
import ProfessorMenuOptions from './sidebar/ProfessorMenuOptions';
import { getProfessorMenuItems, navigateProfessorMenuItem } from './sidebar/menuConfig';

const isProfessorUser = (currentUser) => {
  if (!currentUser) return false;
  const roleName = (currentUser.roleName || currentUser.nombreRol || currentUser.role || '').toLowerCase();
  return roleName === 'profesor' || roleName.includes('profesor') || roleName.includes('profe');
};

const ASSISTANT_LINE_COLORS = [
  '#2563eb',
  '#16a34a',
  '#dc2626',
  '#9333ea',
  '#f59e0b',
  '#0891b2',
  '#db2777',
  '#475569'
];

const toChartNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCompactNumber = (value) => {
  const number = Math.round(toChartNumber(value));
  const abs = Math.abs(number);

  if (abs >= 1000000) {
    return `${Math.round((number / 1000000) * 10) / 10}M`;
  }

  if (abs >= 1000) {
    return `${Math.round((number / 1000) * 10) / 10}k`;
  }

  return number.toString();
};

const formatExactNumber = (value) => Math.round(toChartNumber(value)).toLocaleString('es-AR');

const getSeriesKey = (serie, index) => serie.idOpenAIAssistant || serie.nombre || `serie-${index}`;

const truncateChartLabel = (value, maxLength = 18) => {
  const label = String(value || '');
  return label.length > maxLength ? `${label.slice(0, maxLength - 1)}...` : label;
};

const buildFallbackSeries = (weeklyTotals = []) => {
  if (!weeklyTotals.some((dia) => toChartNumber(dia.conversaciones) > 0)) {
    return [];
  }

  return [{
    idOpenAIAssistant: 'total',
    nombre: 'Total',
    totalMensajes: weeklyTotals.reduce((sum, dia) => sum + toChartNumber(dia.conversaciones), 0),
    puntos: weeklyTotals.map((dia) => ({
      fecha: dia.fecha,
      fechaCompleta: dia.fechaCompleta,
      mensajes: toChartNumber(dia.conversaciones)
    }))
  }];
};

const WeeklyAssistantLineChart = ({ series = [], weeklyTotals = [] }) => {
  const [activePoint, setActivePoint] = useState(null);
  const chartSeries = series.length > 0 ? series : buildFallbackSeries(weeklyTotals);
  const visibleSeries = chartSeries
    .filter((serie) => Array.isArray(serie.puntos) && serie.puntos.length > 0)
    .map((serie) => ({
      ...serie,
      puntos: serie.puntos.map((punto) => ({
        ...punto,
        mensajes: toChartNumber(punto.mensajes)
      }))
    }))
    .sort((a, b) => toChartNumber(b.totalMensajes) - toChartNumber(a.totalMensajes));

  const labels = visibleSeries[0]?.puntos || weeklyTotals || [];

  if (visibleSeries.length === 0 || labels.length === 0) {
    return (
      <div className="assistant-line-chart assistant-line-chart--empty">
        No hay mensajes registrados en los últimos 7 días.
      </div>
    );
  }

  const width = 760;
  const height = 320;
  const margin = { top: 28, right: 34, bottom: 56, left: 58 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const values = visibleSeries.flatMap((serie) => serie.puntos.map((punto) => punto.mensajes));
  const maxValue = Math.max(...values, 0);
  const minValue = Math.min(...values);
  const hasZeroValues = values.some((value) => value === 0);

  let yMin = 0;
  let yMax = maxValue <= 0 ? 1 : maxValue + Math.max(maxValue * 0.08, 1);

  if (maxValue > 0 && !hasZeroValues) {
    const rawRange = Math.max(maxValue - minValue, 1);
    yMin = Math.max(0, minValue - Math.max(rawRange * 0.35, maxValue * 0.03, 1));
    yMax = maxValue + Math.max(rawRange * 0.35, maxValue * 0.04, 1);
  }

  const yRange = Math.max(yMax - yMin, 1);
  const xForIndex = (index) => {
    if (labels.length === 1) {
      return margin.left + plotWidth / 2;
    }
    return margin.left + (index * plotWidth) / (labels.length - 1);
  };
  const yForValue = (value) => {
    const bounded = Math.min(Math.max(value, yMin), yMax);
    return margin.top + ((yMax - bounded) / yRange) * plotHeight;
  };
  const tickValues = Array.from({ length: 5 }, (_, index) => yMin + (yRange * index) / 4);

  const showPointTooltip = (serie, serieIndex, punto, pointIndex, locked = false) => {
    const x = xForIndex(pointIndex);
    const y = yForValue(punto.mensajes);
    const color = ASSISTANT_LINE_COLORS[serieIndex % ASSISTANT_LINE_COLORS.length];
    const seriesMax = Math.max(...serie.puntos.map((item) => item.mensajes));

    setActivePoint({
      key: `${getSeriesKey(serie, serieIndex)}-${punto.fechaCompleta || pointIndex}`,
      assistant: serie.nombre,
      mensajes: punto.mensajes,
      fecha: punto.fecha,
      left: (x / width) * 100,
      top: (y / height) * 100,
      color,
      locked,
      isPeak: punto.mensajes > 0 && punto.mensajes === seriesMax
    });
  };

  const clearUnlockedPoint = () => {
    setActivePoint((current) => (current?.locked ? current : null));
  };

  const tooltipEdgeClass = activePoint?.left < 18
    ? ' assistant-line-chart__tooltip--left'
    : activePoint?.left > 82
      ? ' assistant-line-chart__tooltip--right'
      : '';
  const tooltipVerticalClass = activePoint?.top < 38 ? ' assistant-line-chart__tooltip--below' : '';

  return (
    <div className="assistant-line-chart" onMouseLeave={clearUnlockedPoint}>
      <svg
        className="assistant-line-chart__svg"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Mensajes diarios por asistente en los últimos 7 días"
        onClick={() => setActivePoint(null)}
      >
        {tickValues.map((value) => {
          const y = yForValue(value);
          return (
            <g key={`tick-${value}`} className="assistant-line-chart__tick">
              <line
                x1={margin.left}
                x2={width - margin.right}
                y1={y}
                y2={y}
                className="assistant-line-chart__grid"
              />
              <text x={margin.left - 12} y={y + 4} textAnchor="end" className="assistant-line-chart__axis-label">
                {formatCompactNumber(value)}
              </text>
            </g>
          );
        })}

        <line
          x1={margin.left}
          x2={width - margin.right}
          y1={height - margin.bottom}
          y2={height - margin.bottom}
          className="assistant-line-chart__axis"
        />
        <line
          x1={margin.left}
          x2={margin.left}
          y1={margin.top}
          y2={height - margin.bottom}
          className="assistant-line-chart__axis"
        />

        {labels.map((dia, index) => (
          <g key={dia.fechaCompleta || dia.fecha || index} className="assistant-line-chart__x-label">
            <line
              x1={xForIndex(index)}
              x2={xForIndex(index)}
              y1={height - margin.bottom}
              y2={height - margin.bottom + 6}
              className="assistant-line-chart__axis"
            />
            <text x={xForIndex(index)} y={height - margin.bottom + 24} textAnchor="middle">
              {dia.fecha}
            </text>
          </g>
        ))}

        {visibleSeries.map((serie, serieIndex) => {
          const color = ASSISTANT_LINE_COLORS[serieIndex % ASSISTANT_LINE_COLORS.length];
          const serieKey = getSeriesKey(serie, serieIndex);
          const path = serie.puntos
            .map((punto, pointIndex) => (
              `${pointIndex === 0 ? 'M' : 'L'} ${xForIndex(pointIndex)} ${yForValue(punto.mensajes)}`
            ))
            .join(' ');

          return (
            <g key={serie.idOpenAIAssistant || serie.nombre || serieIndex}>
              <path
                d={path}
                className="assistant-line-chart__line"
                style={{ stroke: color }}
              />
              {serie.puntos.map((punto, pointIndex) => (
                <g key={`${serieKey}-${punto.fechaCompleta || pointIndex}`}>
                  <circle
                    cx={xForIndex(pointIndex)}
                    cy={yForValue(punto.mensajes)}
                    r="4.5"
                    className={`assistant-line-chart__point ${activePoint?.key === `${serieKey}-${punto.fechaCompleta || pointIndex}` ? 'assistant-line-chart__point--active' : ''}`}
                    style={{ fill: color }}
                    tabIndex="0"
                    role="button"
                    aria-label={`${serie.nombre}: ${formatExactNumber(punto.mensajes)} mensajes el ${punto.fecha}`}
                    onMouseEnter={() => showPointTooltip(serie, serieIndex, punto, pointIndex)}
                    onFocus={() => showPointTooltip(serie, serieIndex, punto, pointIndex)}
                    onBlur={clearUnlockedPoint}
                    onClick={(event) => {
                      event.stopPropagation();
                      showPointTooltip(serie, serieIndex, punto, pointIndex, true);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        showPointTooltip(serie, serieIndex, punto, pointIndex, true);
                      }
                    }}
                  />
                </g>
              ))}
            </g>
          );
        })}
      </svg>

      {activePoint && (
        <div
          className={`assistant-line-chart__tooltip${tooltipEdgeClass}${tooltipVerticalClass}`}
          style={{
            left: `${activePoint.left}%`,
            top: `${activePoint.top}%`,
            borderColor: activePoint.color
          }}
        >
          <span className="assistant-line-chart__tooltip-date">{activePoint.fecha}</span>
          <strong>{activePoint.assistant}</strong>
          <span>{formatExactNumber(activePoint.mensajes)} mensajes</span>
          {activePoint.isPeak && <em>Pico del período</em>}
        </div>
      )}

      <div className="assistant-line-chart__legend">
        {visibleSeries.map((serie, index) => (
          <span key={getSeriesKey(serie, index)} className="assistant-line-chart__legend-item">
            <span
              className="assistant-line-chart__legend-color"
              style={{ backgroundColor: ASSISTANT_LINE_COLORS[index % ASSISTANT_LINE_COLORS.length] }}
            />
            <span className="assistant-line-chart__legend-name">{serie.nombre}</span>
            <span className="assistant-line-chart__legend-total">{formatExactNumber(serie.totalMensajes)} mensajes</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const AssistantDetailChart = ({ serie, color, index }) => {
  const [activePoint, setActivePoint] = useState(null);
  const puntos = (serie.puntos || []).map((punto) => ({
    ...punto,
    mensajes: toChartNumber(punto.mensajes)
  }));

  const width = 340;
  const height = 160;
  const margin = { top: 22, right: 18, bottom: 34, left: 38 };
  const plotWidth = width - margin.left - margin.right;
  const plotHeight = height - margin.top - margin.bottom;
  const values = puntos.map((punto) => punto.mensajes);
  const maxValue = Math.max(...values, 0);
  const minValue = Math.min(...values, maxValue);
  const rawRange = Math.max(maxValue - minValue, 1);
  const yMin = maxValue <= 0
    ? 0
    : Math.max(0, minValue - Math.max(rawRange * 0.22, maxValue * 0.06, 1));
  const yMax = maxValue <= 0
    ? 1
    : maxValue + Math.max(rawRange * 0.28, maxValue * 0.08, 1);
  const yRange = Math.max(yMax - yMin, 1);

  const xForIndex = (pointIndex) => {
    if (puntos.length === 1) {
      return margin.left + plotWidth / 2;
    }
    return margin.left + (pointIndex * plotWidth) / (puntos.length - 1);
  };

  const yForValue = (value) => {
    const bounded = Math.min(Math.max(value, yMin), yMax);
    return margin.top + ((yMax - bounded) / yRange) * plotHeight;
  };

  const path = puntos
    .map((punto, pointIndex) => `${pointIndex === 0 ? 'M' : 'L'} ${xForIndex(pointIndex)} ${yForValue(punto.mensajes)}`)
    .join(' ');

  const showPointTooltip = (punto, pointIndex, locked = false) => {
    const x = xForIndex(pointIndex);
    const y = yForValue(punto.mensajes);
    const seriesMax = Math.max(...values, 0);

    setActivePoint({
      key: `${punto.fechaCompleta || pointIndex}`,
      mensajes: punto.mensajes,
      fecha: punto.fecha,
      left: (x / width) * 100,
      top: (y / height) * 100,
      locked,
      isPeak: punto.mensajes > 0 && punto.mensajes === seriesMax
    });
  };

  const clearUnlockedPoint = () => {
    setActivePoint((current) => (current?.locked ? current : null));
  };

  return (
    <div className="assistant-detail-card" onMouseLeave={clearUnlockedPoint}>
      <div className="assistant-detail-card__header">
        <span className="assistant-detail-card__color" style={{ backgroundColor: color }} />
        <div>
          <h3>{serie.nombre || `Asistente ${index + 1}`}</h3>
          <span>{formatExactNumber(serie.totalMensajes)} mensajes</span>
        </div>
      </div>

      <div className="assistant-detail-chart">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          className="assistant-detail-chart__svg"
          role="img"
          aria-label={`Mensajes diarios de ${serie.nombre}`}
          onClick={() => setActivePoint(null)}
        >
          <line
            x1={margin.left}
            x2={width - margin.right}
            y1={height - margin.bottom}
            y2={height - margin.bottom}
            className="assistant-detail-chart__axis"
          />
          <line
            x1={margin.left}
            x2={width - margin.right}
            y1={margin.top}
            y2={margin.top}
            className="assistant-detail-chart__grid"
          />
          <text x={margin.left - 8} y={margin.top + 4} textAnchor="end" className="assistant-detail-chart__axis-label">
            {formatCompactNumber(maxValue)}
          </text>
          <text x={margin.left - 8} y={height - margin.bottom + 4} textAnchor="end" className="assistant-detail-chart__axis-label">
            {formatCompactNumber(Math.max(0, Math.floor(yMin)))}
          </text>

          <path d={path} className="assistant-detail-chart__line" style={{ stroke: color }} />

          {puntos.map((punto, pointIndex) => (
            <g key={punto.fechaCompleta || pointIndex}>
              <circle
                cx={xForIndex(pointIndex)}
                cy={yForValue(punto.mensajes)}
                r="4"
                className={`assistant-detail-chart__point ${activePoint?.key === `${punto.fechaCompleta || pointIndex}` ? 'assistant-detail-chart__point--active' : ''}`}
                style={{ fill: color }}
                tabIndex="0"
                role="button"
                aria-label={`${serie.nombre}: ${formatExactNumber(punto.mensajes)} mensajes el ${punto.fecha}`}
                onMouseEnter={() => showPointTooltip(punto, pointIndex)}
                onFocus={() => showPointTooltip(punto, pointIndex)}
                onBlur={clearUnlockedPoint}
                onClick={(event) => {
                  event.stopPropagation();
                  showPointTooltip(punto, pointIndex, true);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    showPointTooltip(punto, pointIndex, true);
                  }
                }}
              />
              <text x={xForIndex(pointIndex)} y={height - 10} textAnchor="middle" className="assistant-detail-chart__x-label">
                {punto.fecha}
              </text>
            </g>
          ))}
        </svg>

        {activePoint && (
          <div
            className={`assistant-detail-chart__tooltip${activePoint.top < 34 ? ' assistant-detail-chart__tooltip--below' : ''}`}
            style={{
              left: `${activePoint.left}%`,
              top: `${activePoint.top}%`,
              borderColor: color
            }}
          >
            <span>{activePoint.fecha}</span>
            <strong>{formatExactNumber(activePoint.mensajes)} mensajes</strong>
            {activePoint.isPeak && <em>Pico</em>}
          </div>
        )}
      </div>
    </div>
  );
};

const AssistantDetailCharts = ({ series = [] }) => {
  const visibleSeries = series
    .filter((serie) => toChartNumber(serie.totalMensajes) > 0 && Array.isArray(serie.puntos) && serie.puntos.length > 0)
    .sort((a, b) => toChartNumber(b.totalMensajes) - toChartNumber(a.totalMensajes));

  if (visibleSeries.length === 0) return null;

  return (
    <div className="assistant-detail-panel">
      <div className="assistant-detail-panel__header">
        <h3>Detalle por asistente</h3>
      </div>
      <div className="assistant-detail-grid">
        {visibleSeries.map((serie, index) => (
          <AssistantDetailChart
            key={getSeriesKey(serie, index)}
            serie={serie}
            index={index}
            color={ASSISTANT_LINE_COLORS[index % ASSISTANT_LINE_COLORS.length]}
          />
        ))}
      </div>
    </div>
  );
};

const AssistantShareDonutChart = ({ series = [] }) => {
  const [activeSlice, setActiveSlice] = useState(null);
  const visibleSeries = series
    .filter((serie) => toChartNumber(serie.totalMensajes) > 0)
    .sort((a, b) => toChartNumber(b.totalMensajes) - toChartNumber(a.totalMensajes));

  const total = visibleSeries.reduce((sum, serie) => sum + toChartNumber(serie.totalMensajes), 0);

  if (visibleSeries.length === 0 || total <= 0) {
    return (
      <div className="assistant-share-chart assistant-share-chart--empty">
        Sin datos de participación.
      </div>
    );
  }

  const size = 180;
  const center = size / 2;
  const radius = 62;
  const strokeWidth = 24;
  const circumference = 2 * Math.PI * radius;
  let cumulative = 0;
  const active = activeSlice || {
    nombre: 'Total',
    totalMensajes: total,
    porcentaje: 100
  };

  return (
    <div className="assistant-share-chart">
      <div className="assistant-share-chart__header">
        <h3>Participación del período</h3>
      </div>

      <div className="assistant-share-chart__body">
        <svg
          className="assistant-share-chart__svg"
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          aria-label="Participación de mensajes por asistente"
          onClick={() => setActiveSlice(null)}
        >
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="#e2e8f0"
            strokeWidth={strokeWidth}
          />
          {visibleSeries.map((serie, index) => {
            const value = toChartNumber(serie.totalMensajes);
            const percentage = (value / total) * 100;
            const dashLength = (percentage / 100) * circumference;
            const dashOffset = -cumulative;
            cumulative += dashLength;
            const color = ASSISTANT_LINE_COLORS[index % ASSISTANT_LINE_COLORS.length];
            const slice = {
              nombre: serie.nombre,
              totalMensajes: value,
              porcentaje: percentage,
              color
            };

            return (
              <circle
                key={getSeriesKey(serie, index)}
                cx={center}
                cy={center}
                r={radius}
                fill="none"
                stroke={color}
                strokeWidth={strokeWidth}
                strokeDasharray={`${dashLength} ${circumference - dashLength}`}
                strokeDashoffset={dashOffset}
                transform={`rotate(-90 ${center} ${center})`}
                className="assistant-share-chart__slice"
                tabIndex="0"
                role="button"
                aria-label={`${serie.nombre}: ${formatExactNumber(value)} mensajes, ${percentage.toFixed(1)} por ciento`}
                onMouseEnter={() => setActiveSlice(slice)}
                onFocus={() => setActiveSlice(slice)}
                onBlur={() => setActiveSlice(null)}
                onClick={(event) => {
                  event.stopPropagation();
                  setActiveSlice(slice);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setActiveSlice(slice);
                  }
                }}
              />
            );
          })}
          <text x={center} y={center - 6} textAnchor="middle" className="assistant-share-chart__center-name">
            {truncateChartLabel(active.nombre)}
          </text>
          <text x={center} y={center + 18} textAnchor="middle" className="assistant-share-chart__center-value">
            {formatExactNumber(active.totalMensajes)}
          </text>
          <text x={center} y={center + 38} textAnchor="middle" className="assistant-share-chart__center-percent">
            {active.porcentaje.toFixed(1)}%
          </text>
        </svg>

        <div className="assistant-share-chart__legend">
          {visibleSeries.map((serie, index) => {
            const value = toChartNumber(serie.totalMensajes);
            const percentage = (value / total) * 100;
            const color = ASSISTANT_LINE_COLORS[index % ASSISTANT_LINE_COLORS.length];

            return (
              <button
                key={getSeriesKey(serie, index)}
                type="button"
                className="assistant-share-chart__legend-item"
                onMouseEnter={() => setActiveSlice({
                  nombre: serie.nombre,
                  totalMensajes: value,
                  porcentaje: percentage,
                  color
                })}
                onFocus={() => setActiveSlice({
                  nombre: serie.nombre,
                  totalMensajes: value,
                  porcentaje: percentage,
                  color
                })}
                onBlur={() => setActiveSlice(null)}
                onClick={() => setActiveSlice({
                  nombre: serie.nombre,
                  totalMensajes: value,
                  porcentaje: percentage,
                  color
                })}
              >
                <span className="assistant-share-chart__legend-color" style={{ backgroundColor: color }} />
                <span className="assistant-share-chart__legend-name">{serie.nombre}</span>
                <strong>{formatExactNumber(value)}</strong>
                <span>{percentage.toFixed(1)}%</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const NewDashboard = () => {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleApiError } = useApiErrorHandler();
  const isProfesor = isProfessorUser(user);
  const { featureFlags } = useRegionalFeatureFlags(user, { enabled: !authLoading });
  const isGptClassicEnabled = hasGptClassicFeature(featureFlags);
  const isOpenAIAssistantEnabled = hasOpenAIAssistantFeature(featureFlags);
  const isRagFlowAssistantEnabled = hasRagFlowAssistantFeature(featureFlags);
  
  const [datosFormateados, setDatosFormateados] = useState(null);
  const [tendencias, setTendencias] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'error', title: '' });
  const [activeMenuItem, setActiveMenuItem] = useState('dashboard');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  // Ref para evitar múltiples llamadas en React Strict Mode
  const isDataLoaded = useRef(false);
  const hasLoggedRole = useRef(false);

  useEffect(() => {
    if (user && !hasLoggedRole.current) {
      console.group('[NewDashboard] Debug Rol');
      console.log('Usuario:', user);
      console.log('roleName:', user?.roleName);
      console.log('rol (string):', user?.role);
      console.log('roleId:', user?.roleId);
      console.log('isProfesor flag:', isProfesor);
      console.groupEnd();
      hasLoggedRole.current = true;
    }
  }, [user, isProfesor]);

  // Cargar estadísticas al montar el componente
  useEffect(() => {
    if (authLoading) return;

    if (!isProfesor) {
      setIsLoading(false);
      return;
    }

    if (!isDataLoaded.current) {
      isDataLoaded.current = true;
      cargarEstadisticas();
    }
  }, [isProfesor, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (location.state?.deniedOpenAIAssistantFeature) {
      setAlert({
        show: true,
        title: 'Funcionalidad deshabilitada',
        message: 'El feature flag de Asistentes OpenAI no está habilitado para esta regional.',
        type: 'warning'
      });
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (location.state?.deniedRagFlowAssistantFeature) {
      setAlert({
        show: true,
        title: 'Funcionalidad deshabilitada',
        message: 'El feature flag de Asistentes AVE no esta habilitado para esta regional.',
        type: 'warning'
      });
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    if (location.state?.deniedCreateAssistant) {
      setAlert({
        show: true,
        title: 'Permiso denegado',
        message: 'No tienes permisos para crear asistentes. Contacta al administrador.',
        type: 'warning'
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  const cargarEstadisticas = async () => {
    if (authLoading) return;
    if (!isProfesor) {
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      
      // Obtener estadísticas del backend
      const estadisticasData = await dashboardService.getEstadisticas();
      
      // Formatear datos para gráficos
      const datosFormateados = dashboardService.formatearDatosParaGraficos(estadisticasData);
      setDatosFormateados(datosFormateados);
      
      // Calcular tendencias
      const tendenciasData = dashboardService.calcularTendencias(datosFormateados);
      setTendencias(tendenciasData);
      
    } catch (error) {
      console.error('Error al cargar estadísticas:', error);
      
      try {
        // Usar el manejador de errores personalizado para errores 401
        handleApiError(error);
      } catch (handledError) {
        // Si el error no es 401, mostrar mensaje de error
        setAlert({
          show: true,
          message: handledError.message || error.message || 'Error al cargar las estadísticas del dashboard',
          type: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="dashboard-container">
        <div className="loading-container">
          <LoadingSpinner message="Verificando permisos..." />
        </div>
      </div>
    );
  }

  if (!isProfesor) {
    return (
      <div className="dashboard-container">
        <div
          style={{
            maxWidth: '640px',
            margin: '0 auto',
            padding: '2rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.1)',
            textAlign: 'center'
          }}
        >
          <h2>Acceso restringido</h2>
          <p>Este dashboard está disponible únicamente para usuarios con rol Profesor.</p>
        </div>
      </div>
    );
  }

  const closeAlert = () => {
    setAlert(prev => ({ ...prev, show: false }));
  };

  const menuItems = getProfessorMenuItems({ user, isGptClassicEnabled, isOpenAIAssistantEnabled, isRagFlowAssistantEnabled });

  // Generar tarjetas del dashboard con datos reales
  const getDashboardCards = () => {
    if (!datosFormateados) {
      return [
        { title: 'Asistentes IA', value: '-', icon: 'fas fa-robot', color: 'primary' },
        { title: 'Conversaciones Activas', value: '-', icon: 'fas fa-comments', color: 'secondary' },
      ];
    }

    const { resumenPrincipal } = datosFormateados;
    return [
      { 
        title: 'Asistentes IA', 
        value: resumenPrincipal.totalAsistentes?.toString() || '0', 
        icon: 'fas fa-robot', 
        color: 'primary',
        subtitle: `${resumenPrincipal.asistenteMasUsado !== 'Ninguno' ? `Más usado: ${resumenPrincipal.asistenteMasUsado}` : 'Sin uso registrado'}`
      },
      { 
        title: 'Conversaciones Este Mes', 
        value: resumenPrincipal.conversacionesMes?.toString() || '0', 
        icon: 'fas fa-comments', 
        color: 'secondary',
        subtitle: `Hoy: ${resumenPrincipal.conversacionesHoy || 0} | Semana: ${resumenPrincipal.conversacionesSemana || 0}`
      },
    ];
  };

  const handleNewAssistant = () => {
    if (!user?.pertmiteCrearAsistente) {
      setAlert({
        show: true,
        title: 'Permiso denegado',
        message: 'No tienes permisos para crear asistentes. Contacta al administrador.',
        type: 'warning'
      });
      return;
    }
    if (!isOpenAIAssistantEnabled) {
      setAlert({
        show: true,
        title: 'Funcionalidad deshabilitada',
        message: 'El feature flag de Asistentes OpenAI no está habilitado para esta regional.',
        type: 'warning'
      });
      return;
    }

    navigate('/new-assistant');
  };

  const handleNewRagFlowAssistant = () => {
    if (!user?.pertmiteCrearAsistente) {
      setAlert({
        show: true,
        title: 'Permiso denegado',
        message: 'No tienes permisos para crear asistentes. Contacta al administrador.',
        type: 'warning'
      });
      return;
    }
    if (!isRagFlowAssistantEnabled) {
      setAlert({
        show: true,
        title: 'Funcionalidad deshabilitada',
        message: 'El feature flag de Asistentes AVE no esta habilitado para esta regional.',
        type: 'warning'
      });
      return;
    }

    navigate('/new-ragflow-assistant');
  };

  const handleNewGptAssistant = () => {
    if (!user?.pertmiteCrearAsistente) {
      setAlert({
        show: true,
        title: 'Permiso denegado',
        message: 'No tienes permisos para crear asistentes. Contacta al administrador.',
        type: 'warning'
      });
      return;
    }
    if (!isGptClassicEnabled) {
      setAlert({
        show: true,
        title: 'Funcionalidad deshabilitada',
        message: 'El feature flag de Asistentes GPT no está habilitado para esta regional.',
        type: 'warning'
      });
      return;
    }

    navigate('/new-gpt-assistant');
  };
  const handleMenuItemClick = (itemId) => {
    setActiveMenuItem(itemId);
    setIsSidebarOpen(false);

    navigateProfessorMenuItem(navigate, itemId);
  };

  return (
    <div className={`new-dashboard ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>UTN FRC</span>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          <ProfessorMenuOptions
            user={user}
            isGptClassicEnabled={isGptClassicEnabled}
            isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
            isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
            activeItemId={activeMenuItem}
            onSelect={(item) => handleMenuItemClick(item.id)}
          />
        </nav>
        
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">{user?.rol || user?.role || 'Profesor'}</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Alternar menú lateral"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">
              {menuItems.find(item => item.id === activeMenuItem)?.label || 'Dashboard'}
            </h1>
          </div>
          
          <div className="header-right">
            <ActiveRegionalBadge />
            <SolicitudesProcesosBadge />
            <ProcesosMenu />
            <UserAccountMenu />
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Alert */}
          {alert.show && (
            <Alert
              title={alert.title}
              message={alert.message}
              type={alert.type}
              onClose={closeAlert}
            />
          )}

          {/* Nuevo Asistente Button - Ubicado en la parte superior */}
          {user?.pertmiteCrearAsistente && (isOpenAIAssistantEnabled || isRagFlowAssistantEnabled || isGptClassicEnabled) && (
            <div className="new-assistant-section-top">
              {isOpenAIAssistantEnabled && (
                <button className="new-assistant-btn-top" onClick={handleNewAssistant}>
                  <div className="btn-icon">
                    <i className="fas fa-plus"></i>
                  </div>
                  <div className="btn-content">
                    <span className="btn-title">Nuevo Asistente OpenAI</span>
                    <span className="btn-subtitle">Crear un asistente OpenAI personalizado</span>
                  </div>
                </button>
              )}
              {isRagFlowAssistantEnabled && (
                <button className="new-assistant-btn-top new-assistant-btn-top--secondary" onClick={handleNewRagFlowAssistant}>
                  <div className="btn-icon">
                    <i className="fas fa-plus"></i>
                  </div>
                  <div className="btn-content">
                    <span className="btn-title">Nuevo Asistente AVE</span>
                    <span className="btn-subtitle">Crear un asistente AVE</span>
                  </div>
                </button>
              )}
              {isGptClassicEnabled && (
                <button className="new-assistant-btn-top new-assistant-btn-top--secondary" onClick={handleNewGptAssistant}>
                  <div className="btn-icon">
                    <i className="fas fa-plus"></i>
                  </div>
                  <div className="btn-content">
                    <span className="btn-title">Nuevo Asistente GPT</span>
                    <span className="btn-subtitle">Crear un asistente GPT clásico</span>
                  </div>
                </button>
              )}
            </div>
          )}

          {activeMenuItem === 'dashboard' && (
            <div className="dashboard-overview">
              {/* Loading Spinner */}
              {isLoading ? (
                <div className="loading-container">
                  <LoadingSpinner message="Obteniendo estadísticas del dashboard..." />
                </div>
              ) : (
                <>
                  {/* Stats Cards */}
                  <div className="stats-grid">
                    {getDashboardCards().map((card, index) => (
                      <div key={index} className={`stats-card ${card.color}`}>
                        <div className="card-icon">
                          <i className={card.icon}></i>
                        </div>
                        <div className="card-content">
                          <h3 className="card-value">{card.value}</h3>
                          <p className="card-title">{card.title}</p>
                          {card.subtitle && <p className="card-subtitle">{card.subtitle}</p>}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Gráfico de actividad de los últimos 7 días */}
                  {datosFormateados?.actividadSemanal?.length > 0 && (
                    <div className="dashboard-section">
                      <div className="section-header">
                        <h2 className="section-title">
                          <i className="fas fa-chart-line"></i>
                          Mensajes por Asistente de los Últimos 7 Días
                        </h2>
                      </div>
                      <div className="assistant-activity-layout">
                        <div className="chart-container chart-container--assistant-main">
                          <WeeklyAssistantLineChart
                            series={datosFormateados.actividadPorAsistente || []}
                            weeklyTotals={datosFormateados.actividadSemanal || []}
                          />
                        </div>
                        <AssistantShareDonutChart series={datosFormateados.actividadPorAsistente || []} />
                      </div>
                      <AssistantDetailCharts series={datosFormateados.actividadPorAsistente || []} />
                    </div>
                  )}

                  {/* Métricas Adicionales */}
                  {tendencias && (
                    <div className="dashboard-section">
                      <div className="section-header">
                        <h2 className="section-title">
                          <i className="fas fa-analytics"></i>
                          Resumen de Tendencias
                        </h2>
                      </div>
                      <div className="metrics-grid">
                        <div className="metric-card">
                          <div className="metric-icon">
                            <i className="fas fa-chart-line"></i>
                          </div>
                          <div className="metric-content">
                            <h4>Promedio Diario</h4>
                            <span className="metric-value">{tendencias.promedioDiario}</span>
                            <small>conversaciones por día</small>
                          </div>
                        </div>
                        <div className="metric-card">
                          <div className="metric-icon">
                            <i className="fas fa-crown"></i>
                          </div>
                          <div className="metric-content">
                            <h4>Día Más Activo</h4>
                            <span className="metric-value">{tendencias.diaMasActivo.fecha}</span>
                            <small>{tendencias.diaMasActivo.conversaciones} conversaciones</small>
                          </div>
                        </div>
                        <div className="metric-card">
                          <div className="metric-icon">
                            <i className={`fas ${tendencias.tasaCrecimiento >= 0 ? 'fa-arrow-up text-success' : 'fa-arrow-down text-danger'}`}></i>
                          </div>
                          <div className="metric-content">
                            <h4>Crecimiento</h4>
                            <span className={`metric-value ${tendencias.tasaCrecimiento >= 0 ? 'text-success' : 'text-danger'}`}>
                              {tendencias.tasaCrecimiento >= 0 ? '+' : ''}{tendencias.tasaCrecimiento}%
                            </span>
                            <small>mes vs semana</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}



              {/* Recent Activity */}
              
            </div>
          )}

          {activeMenuItem !== 'dashboard' && (
            <div className="content-placeholder">
              <div className="placeholder-icon">
                <i className={menuItems.find(item => item.id === activeMenuItem)?.icon}></i>
              </div>
              <h2>Sección en Desarrollo</h2>
              <p>Esta sección estará disponible próximamente.</p>
            </div>
          )}
        </div>
      </main>

    </div>
  );
};

export default NewDashboard;
